import React, { useState, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	Textarea,
	Text,
	HStack,
	useToast,
	Icon,
	InputGroup,
	InputLeftElement,
	Divider,
	SimpleGrid,
	Flex,
	Card,
	CardBody,
	CardHeader,
	VStack,
	useColorModeValue,
	Avatar,
	Checkbox,
	Badge,
	Select,
	Switch,
} from '@chakra-ui/react';
import {
	FaSave,
	FaTimes,
	FaCalendarAlt,
	FaTasks,
	FaSearch,
} from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import * as todoApi from '../../Api/todoApi.js';
import { useEmployees } from '../../hooks/useEmployee';
import {
	isReadyToReviewStatus,
	WORK_ITEM_STATUS_FORM_OPTIONS,
	getTodoCreatorId,
	hasNonCreatorAssignees,
	isTodoPrivate,
	canTodoBePrivate,
} from './WorkspaceViews/taskViewUtils';

// Helper component for Segmented Controls (Status & Priority)
const SegmentedControl = ({ options, value, onChange, colorMap }) => {
	const bg = useColorModeValue('gray.100', 'gray.700');

	return (
		<Flex bg={bg} p={1} borderRadius='lg' w='full' flexWrap='wrap' gap={1}>
			{options.map((opt) => {
				const isSelected = value === opt.value;
				const activeColor = colorMap?.[opt.value] || 'blue';
				return (
					<Box
						key={opt.value}
						flex={1}
						textAlign='center'
						py={2}
						px={3}
						borderRadius='md'
						cursor='pointer'
						bg={isSelected ? 'white' : 'transparent'}
						color={isSelected ? `${activeColor}.600` : 'gray.500'}
						fontWeight={isSelected ? 'bold' : 'medium'}
						boxShadow={isSelected ? 'sm' : 'none'}
						transition='all 0.2s'
						onClick={() => onChange(opt.value)}
						_dark={{
							bg: isSelected ? 'gray.600' : 'transparent',
							color: isSelected ? `${activeColor}.300` : 'gray.400',
						}}>
						{opt.label}
					</Box>
				);
			})}
		</Flex>
	);
};

const TodoForm = ({
	todo,
	onSave,
	onCancel,
	showTitle = true,
	companyId: propCompanyId,
	employeeId,
	initialStatus,
}) => {
	const {
		userId,
		companyId: contextCompanyId,
	} = useAuthenticatedUserInfo();
	const finalCompanyId = propCompanyId || contextCompanyId;
	const { data: employees = [], isLoading: loading, error } = useEmployees(); // No parameters needed - API auto-filters by logged-in user's company
	const toast = useToast();

	const [formData, setFormData] = useState({
		description: '',
		status: 'pending',
		toCompleteBy: '',
		assignedTo: [],
		priority: 'low',
		reviewer: '',
		isPublic: false,
	});
	const [searchText, setSearchText] = useState('');
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	// Filter employees based on search text
	const filteredEmployees = employees.filter((emp) => {
		const fullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
		return fullName.toLowerCase().includes(searchText.toLowerCase());
	});

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			return () => document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [isOpen]);

	// Theming
	const cardBg = useColorModeValue('white', 'gray.800');
	const sectionBg = useColorModeValue('gray.50', 'gray.700');
	const borderColor = useColorModeValue('gray.200', 'gray.600');
	const assigneeCheckedBg = useColorModeValue('blue.50', 'blue.900');
	const assigneeCheckedHoverBg = useColorModeValue('blue.100', 'blue.800');
	const assigneeHoverBg = useColorModeValue('gray.100', 'gray.600');

	const formatDate = (date) =>
		date ? new Date(date).toISOString().split('T')[0] : '';

	const creatorId = todo ? getTodoCreatorId(todo) : String(userId);
	const normalizeAssigneeIds = (ids) => Array.from(new Set(ids.map(String)));
	const ensureCreatorInAssignees = (assignedTo) => {
		if (!creatorId) {
			return normalizeAssigneeIds(assignedTo);
		}
		return normalizeAssigneeIds([...assignedTo, creatorId]);
	};

	useEffect(() => {
		if (todo) {
			const assignedIds = ensureCreatorInAssignees(
				todo.assignedTo?.map((employee) => String(employee._id || employee)) || [],
			);
			setFormData({
				description: todo.description || '',
				status: todo.status === 'completed' ? 'reviewed & completed' : todo.status || 'pending',
				toCompleteBy: formatDate(todo.toCompleteBy),
				assignedTo: assignedIds,
				priority: todo.priority || 'low',
				reviewer: todo.reviewer?._id || todo.reviewer || '',
				isPublic: !isTodoPrivate(todo),
			});
			return;
		}

		setFormData({
			description: '',
			status: initialStatus || 'pending',
			toCompleteBy: '',
			assignedTo: creatorId ? [creatorId] : [],
			priority: 'low',
			reviewer: '',
			isPublic: false,
		});
	}, [todo, initialStatus, creatorId]);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({ ...prevData, [name]: value }));
	};

	const handleCustomChange = (name, value) => {
		setFormData((prevData) => {
			const next = { ...prevData, [name]: value };
			if (name === 'status' && !isReadyToReviewStatus(value)) {
				next.reviewer = '';
			}
			return next;
		});
	};

	const todoContext = todo || { users: [{ _id: creatorId }] };
	const hasOtherAssignees = hasNonCreatorAssignees(todoContext, formData.assignedTo);

	const toggleAssignee = (employeeId) => {
		const id = String(employeeId);
		if (id === creatorId) return;
		setFormData((prev) => {
			const isAssigned = prev.assignedTo.some((entry) => String(entry) === id);
			const newAssignedTo = isAssigned
				? prev.assignedTo.filter((entry) => String(entry) !== id)
				: [...prev.assignedTo, id];
			const withCreator = ensureCreatorInAssignees(newAssignedTo);
			const addedOther = !isAssigned;
			return {
				...prev,
				assignedTo: withCreator,
				isPublic: addedOther ? true : prev.isPublic,
			};
		});
	};

	const handlePublicToggle = (event) => {
		const nextPublic = event.target.checked;
		if (!nextPublic && !canTodoBePrivate(todoContext, formData.assignedTo)) {
			toast({
				title: 'Remove other assignees first',
				description:
					'This todo is shared with other people. Remove them before making it private.',
				status: 'warning',
				duration: 4000,
				isClosable: true,
				position: 'top-right',
			});
			return;
		}
		setFormData((prev) => ({ ...prev, isPublic: nextPublic }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (isReadyToReviewStatus(formData.status) && !formData.reviewer) {
			toast({
				title: 'Reviewer required',
				description: 'Please select a reviewer before marking this item as ready to review.',
				status: 'warning',
				duration: 4000,
				isClosable: true,
				position: 'top-right',
			});
			return;
		}
		try {
			const assignedTo = ensureCreatorInAssignees(formData.assignedTo);
			const todoData = {
				description: formData.description,
				status: formData.status,
				toCompleteBy: formData.toCompleteBy || undefined,
				priority: formData.priority,
				private: hasNonCreatorAssignees(todoContext, assignedTo)
					? false
					: !formData.isPublic,
				assignedTo,
				reviewer: isReadyToReviewStatus(formData.status) ? formData.reviewer : null,
			};
			if (todo) {
				await todoApi.updateTodo(todo._id, todoData, userId, finalCompanyId);
				toast({
					title: 'Task Updated',
					status: 'success',
					duration: 3000,
					isClosable: true,
					position: 'top-right',
				});
			} else {
				await todoApi.createTodo(todoData, userId, finalCompanyId);
				toast({
					title: 'Task Created',
					status: 'success',
					duration: 3000,
					isClosable: true,
					position: 'top-right',
				});
			}
			onSave();
		} catch (error) {
			toast({
				title: 'Submission Failed',
				description:
					error?.response?.data?.msg ||
					error?.response?.data?.error ||
					error?.message ||
					'There was an error saving your todo. Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
				position: 'top-right',
			});
		}
	};

	const statusOptions = WORK_ITEM_STATUS_FORM_OPTIONS;
	const statusColors = {
		'not started': 'gray',
		pending: 'orange',
		'in progress': 'blue',
		'ready to review': 'purple',
		'reviewed & completed': 'green',
		completed: 'green',
	};

	const priorityOptions = [
		{ label: 'Low', value: 'low' },
		{ label: 'Medium', value: 'medium' },
		{ label: 'High', value: 'high' },
	];
	const priorityColors = { low: 'green', medium: 'orange', high: 'red' };

	return (
		<Card
			mx='auto'
			as='form'
			onSubmit={handleSubmit}
			bg={cardBg}
			shadow={showTitle ? 'md' : 'none'}
			borderRadius={showTitle ? 'xl' : 'none'}
			border={showTitle ? '1px solid' : 'none'}
			borderColor={borderColor}>
			{showTitle && (
				<CardHeader pb={0} pt={6} px={8}>
					<HStack spacing={3} mb={2}>
						<Flex
							w='40px'
							h='40px'
							bg='blue.50'
							color='blue.500'
							borderRadius='lg'
							align='center'
							justify='center'>
							<Icon as={FaTasks} boxSize={5} />
						</Flex>
						<Box>
							<Text
								fontSize='xl'
								fontWeight='bold'
								color='gray.800'
								_dark={{ color: 'white' }}>
								{todo ? 'Edit Todo' : 'Create New Todo'}
							</Text>
							<Text fontSize='sm' color='gray.500'>
								Fill in the details below to track this item.
							</Text>
						</Box>
					</HStack>
				</CardHeader>
			)}

			<CardBody px={showTitle ? 8 : 0} pt={showTitle ? 6 : 0} pb={showTitle ? 8 : 0}>
				<VStack align='stretch' spacing={6}>
					<FormControl isRequired>
						<FormLabel
							fontWeight='semibold'
							color='gray.700'
							_dark={{ color: 'gray.200' }}>
							Description
						</FormLabel>
						<Textarea
							name='description'
							value={formData.description}
							onChange={handleChange}
							placeholder='What needs to be done?'
							focusBorderColor='blue.400'
							borderRadius='md'
							rows={3}
							resize='vertical'
						/>
					</FormControl>

					<SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
						<FormControl>
							<FormLabel
								fontWeight='semibold'
								color='gray.700'
								_dark={{ color: 'gray.200' }}>
								Complete By
							</FormLabel>
							<InputGroup size='md'>
								<InputLeftElement pointerEvents='none'>
									<Icon as={FaCalendarAlt} color='gray.400' />
								</InputLeftElement>
								<Input
									name='toCompleteBy'
									type='date'
									value={formData.toCompleteBy}
									onChange={handleChange}
									focusBorderColor='blue.400'
									borderRadius='md'
								/>
							</InputGroup>
						</FormControl>

						<FormControl>
							<FormLabel
								fontWeight='semibold'
								color='gray.700'
								_dark={{ color: 'gray.200' }}>
								Priority
							</FormLabel>
							<SegmentedControl
								options={priorityOptions}
								value={formData.priority}
								onChange={(val) => handleCustomChange('priority', val)}
								colorMap={priorityColors}
							/>
						</FormControl>

					</SimpleGrid>

					<FormControl>
						<FormLabel
							fontWeight='semibold'
							color='gray.700'
							_dark={{ color: 'gray.200' }}>
							Status
						</FormLabel>
						<SegmentedControl
							options={statusOptions}
							value={formData.status}
							onChange={(val) => handleCustomChange('status', val)}
							colorMap={statusColors}
						/>
					</FormControl>

					{isReadyToReviewStatus(formData.status) && (
						<FormControl isRequired>
							<FormLabel
								fontWeight='semibold'
								color='gray.700'
								_dark={{ color: 'gray.200' }}>
								Reviewer
							</FormLabel>
							<Select
								placeholder='Select a reviewer'
								value={formData.reviewer}
								onChange={(e) => handleCustomChange('reviewer', e.target.value)}
								size='md'
								borderRadius='md'
								focusBorderColor='purple.400'>
								{employees.map((emp) => (
									<option key={emp._id} value={emp._id}>
										{`${emp.firstName || ''} ${emp.lastName || ''}`.trim() ||
											'Unnamed employee'}
									</option>
								))}
							</Select>
						</FormControl>
					)}

					<FormControl
						display='flex'
						alignItems='center'
						justifyContent='space-between'
						p={4}
						mb={4}
						border='1px solid'
						borderColor={borderColor}
						borderRadius='lg'
						bg={sectionBg}>
						<Box pr={4}>
							<FormLabel htmlFor='todo-public-toggle' mb={1} fontWeight='semibold'>
								Visible to team
							</FormLabel>
							<Text fontSize='xs' color='gray.500'>
								{hasOtherAssignees
									? 'Assigning others keeps this todo public.'
									: 'Let teammates see this todo without assigning them.'}
							</Text>
						</Box>
						<Switch
							id='todo-public-toggle'
							size='lg'
							colorScheme='teal'
							isChecked={formData.isPublic || hasOtherAssignees}
							onChange={handlePublicToggle}
							isDisabled={hasOtherAssignees}
						/>
					</FormControl>

					<Box maxW='70%' mb={6}>
						<FormControl ref={dropdownRef} position='relative'>
							<Flex justify='space-between' align='center' mb={2}>
								<FormLabel
									fontWeight='semibold'
									color='gray.700'
									_dark={{ color: 'gray.200' }}
									mb={0}>
									Assign To
								</FormLabel>
								<Badge colorScheme='blue' borderRadius='full' px={2}>
									{formData.assignedTo.length} selected
								</Badge>
							</Flex>
							<Text fontSize='xs' color='gray.500' mb={3}>
								You are always assigned as the creator. Add others to assign work —
								that also makes the todo public.
							</Text>

							<InputGroup size='md' mb={3}>
								<InputLeftElement pointerEvents='none'>
									<Icon as={FaSearch} color='gray.400' />
								</InputLeftElement>
								<Input
									placeholder='Search team members...'
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									focusBorderColor='blue.400'
									borderRadius='md'
									onFocus={() => setIsOpen(true)}
								/>
							</InputGroup>

							

							{formData.assignedTo.length > 0 && (
								<Flex gap={2} mb={3} wrap='wrap'>
									{formData.assignedTo.map((empId) => {
										const emp = employees.find(
											(e) => String(e._id) === String(empId),
										);
										const isCreator = String(empId) === creatorId;
										return emp ? (
											<Badge
												key={empId}
												colorScheme={isCreator ? 'gray' : 'blue'}
												borderRadius='full'
												px={3}
												py={1}
												display='flex'
												alignItems='center'
												gap={1}>
												<Avatar size='xs' name={`${emp.firstName} ${emp.lastName}`} />
												{emp.firstName} {emp.lastName}
												{isCreator ? (
													<Text as='span' fontSize='10px' opacity={0.8}>
														(creator)
													</Text>
												) : (
													<Icon
														as={FaTimes}
														cursor='pointer'
														onClick={() => toggleAssignee(empId)}
														boxSize={3}
													/>
												)}
											</Badge>
										) : null;
									})}
								</Flex>
							)}

							{isOpen && (
								<Box
									minH='160px'
									maxH='240px'
									overflowY='auto'
									bg={sectionBg}
									border='1px solid'
									borderColor={borderColor}
									borderRadius='lg'
									p={2}
									boxShadow='md'>
									{error ? (
										<Text color='red.500' fontSize='sm' p={3}>
											Failed to load team members.
										</Text>
									) : filteredEmployees.length === 0 ? (
										<Text color='gray.500' fontSize='sm' p={3}>
											{loading ? 'Loading team...' : 'No results found.'}
										</Text>
									) : (
										<VStack align='stretch' spacing={1}>
											{filteredEmployees.map((employee) => {
												const employeeId = String(employee._id);
												const isCreator = employeeId === creatorId;
												const isChecked = formData.assignedTo.some(
													(id) => String(id) === employeeId,
												);
												return (
													<Flex
														key={employeeId}
														as='button'
														type='button'
														p={2}
														borderRadius='md'
														cursor={isCreator ? 'not-allowed' : 'pointer'}
														opacity={isCreator ? 0.7 : 1}
														bg={isChecked ? assigneeCheckedBg : 'transparent'}
														_hover={{
															bg: isChecked ? assigneeCheckedHoverBg : assigneeHoverBg,
														}}
														transition='all 0.2s'
														align='center'
														justify='space-between'
														w='full'
														textAlign='left'
														onMouseDown={(e) => e.preventDefault()}
														onClick={() => !isCreator && toggleAssignee(employeeId)}>
														<HStack spacing={3}>
															<Avatar
																size='sm'
																name={`${employee.firstName} ${employee.lastName}`}
																bg='blue.500'
																color='white'
															/>
															<Text fontSize='sm' fontWeight={isChecked ? 'bold' : 'medium'}>
																{employee.firstName} {employee.lastName}
															</Text>
														</HStack>
														<Checkbox
															isChecked={isChecked}
															onChange={() => toggleAssignee(employeeId)}
															colorScheme='blue'
															size='lg'
															pointerEvents='none'
														/>
													</Flex>
												);
											})}
										</VStack>
									)}
								</Box>
							)}
						</FormControl>
					</Box>
				</VStack>

				<Divider my={6} borderColor={borderColor} />

				<Flex justify='flex-end' gap={3} wrap='wrap'>
					<Button
						variant='ghost'
						colorScheme='gray'
						leftIcon={<FaTimes />}
						onClick={onCancel}
						px={6}
						size='md'
						fontWeight='semibold'>
						Cancel
					</Button>
					<Button
						type='submit'
						colorScheme='blue'
						leftIcon={<FaSave />}
						isLoading={loading}
						boxShadow='sm'
						px={8}
						size='md'
						fontWeight='semibold'
						_hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
						_active={{ transform: 'translateY(0)', boxShadow: 'sm' }}>
						{todo ? 'Save Changes' : 'Create Todo'}
					</Button>
				</Flex>
			</CardBody>
		</Card>
	);
};

export default TodoForm;
